import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import api from '../config/axios';
import ChallengeQr from '../components/ChallengeQr';

const payloadOf = (code) => `${window.location.origin}/hackathon?ref=${code}`;

const saveBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const printBlob = (data) => {
  const url = URL.createObjectURL(data);
  const frame = document.createElement('iframe');
  frame.setAttribute('title', 'Impressão dos QR Codes');
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
      frame.remove();
    }, 1000);
  };
  document.body.appendChild(frame);
};

export default function QrPosterPanel({ eventId }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = async () => {
    const response = await api.get(`/hackathon/admin/events/${eventId}/checkin-posters`);
    setCatalog(response.data);
    setSelected((response.data.posters || []).filter((poster) => poster.linkId).map((poster) => poster.slotKey));
    return response.data;
  };

  useEffect(() => {
    let active = true;
    setError('');
    api.get(`/hackathon/admin/events/${eventId}/checkin-posters`)
      .then((response) => {
        if (!active) return;
        setCatalog(response.data);
        setSelected((response.data.posters || []).filter((poster) => poster.linkId).map((poster) => poster.slotKey));
      })
      .catch((reason) => {
        if (active) setError(reason?.response?.data?.message || 'Não foi possível carregar os QR Codes.');
      });
    return () => {
      active = false;
    };
  }, [eventId]);

  const posters = catalog?.posters || [];
  const chosen = posters.filter((poster) => selected.includes(poster.slotKey) && poster.linkId);

  const toggle = (slotKey) => {
    setSelected((current) => (
      current.includes(slotKey) ? current.filter((key) => key !== slotKey) : [...current, slotKey]
    ));
  };

  const ensure = async () => {
    setBusy('ensure');
    setError('');
    try {
      const response = await api.post(`/hackathon/admin/events/${eventId}/checkin-posters`, {});
      setCatalog(response.data);
      setSelected((response.data.posters || []).map((poster) => poster.slotKey));
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível gerar os QR Codes.');
    } finally {
      setBusy('');
    }
  };

  const pdf = async (linkIds, layout, mode) => {
    setBusy(mode);
    setError('');
    try {
      const response = await api.post(
        `/hackathon/admin/events/${eventId}/checkin-posters/pdf`,
        { linkIds, layout, publicOrigin: window.location.origin },
        { responseType: 'blob' }
      );
      if (mode === 'print') printBlob(response.data);
      else saveBlob(response.data, layout === 'single' ? 'qr-presenca.pdf' : 'folha-qr-presenca.pdf');
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível gerar o PDF.');
    } finally {
      setBusy('');
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h3" component="h2">QR para impressão</Typography>
      <Typography variant="body2" color="text.secondary">
        Cada cartaz identifica o evento, o dia ou a atividade. O mesmo QR vale para entrada e saída:
        a pessoa escolhe a operação no celular. O servidor só grava a presença na janela, no GPS e na inscrição corretos.
      </Typography>
      {catalog && catalog.event?.publicPresence === false ? (
        <Alert severity="warning">
          O evento ainda não está com presença pública. Os cartazes podem ser preparados, mas a leitura só passa a abrir depois da publicação.
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
        <Button variant="contained" disabled={busy !== ''} onClick={ensure}>
          {busy === 'ensure' ? 'Gerando...' : 'Gerar códigos que faltam'}
        </Button>
        <Button variant="outlined" disabled={chosen.length === 0 || busy !== ''} onClick={() => pdf(chosen.map((poster) => poster.linkId), 'sheet', 'download')}>
          Baixar folha A4
        </Button>
        <Button variant="outlined" disabled={chosen.length === 0 || busy !== ''} onClick={() => pdf(chosen.map((poster) => poster.linkId), 'sheet', 'print')}>
          Imprimir selecionados
        </Button>
        <Button variant="text" disabled={busy !== ''} onClick={() => load().catch((reason) => setError(reason?.response?.data?.message || 'Não foi possível atualizar.'))}>
          Atualizar
        </Button>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {posters.map((poster) => (
          <Box key={poster.slotKey} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                inputProps={{ 'aria-label': `Selecionar ${poster.title} ${poster.dateLabel}` }}
                checked={selected.includes(poster.slotKey)}
                onChange={() => toggle(poster.slotKey)}
                disabled={!poster.linkId}
              />
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1, alignItems: 'center' }}>
                <Typography variant="overline">PontoNow</Typography>
                {poster.publicCode ? <ChallengeQr payload={payloadOf(poster.publicCode)} /> : (
                  <Typography variant="body2">Código ainda não gerado.</Typography>
                )}
                <Typography variant="subtitle1" component="p" sx={{ textAlign: 'center' }}>{poster.title}</Typography>
                <Typography variant="body2">{poster.dateLabel}</Typography>
                {poster.timeLabel ? <Typography variant="body2">{poster.timeLabel}</Typography> : null}
                {poster.place ? <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>{poster.place}</Typography> : null}
                <Typography variant="body2">{poster.operationLabel}</Typography>
                <Button size="small" disabled={!poster.linkId || busy !== ''} onClick={() => pdf([poster.linkId], 'single', 'download')}>
                  PDF individual
                </Button>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
